export const DEFAULT_MISSION_TITLE = 'Daily Mission';
export const MAX_TASKS = 10;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function isDayKey(value) {
  if (!DAY_KEY.test(String(value || ''))) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** `2026-10-08` → `Thu, 08 Oct 2026`. Uses UTC so the browser timezone never shifts the day. */
export function formatMissionDate(dayKey) {
  if (!isDayKey(dayKey)) return dayKey || '—';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[date.getUTCDay()]}, ${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

export function addDays(dayKey, days) {
  if (!isDayKey(dayKey)) return '';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Browser local date; only a fallback when the API did not send `today`. */
export function localToday() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function compareMissionsNewestFirst(a, b) {
  return String(b.mission_date || '').localeCompare(String(a.mission_date || ''));
}

export function dayStatus(missionDate, today) {
  if (!isDayKey(missionDate) || !isDayKey(today)) return 'upcoming';
  if (missionDate === today) return 'today';
  return missionDate < today ? 'past' : 'upcoming';
}

export function parseActionOptions(payload) {
  const raw = payload?.actions || payload;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((action) => {
      if (typeof action === 'string') return { action_key: action, label: action, default_link: '' };
      const key = action.action_key || '';
      return {
        action_key: key,
        label: action.label || key,
        description: action.description || '',
        default_link: action.default_deep_link || action.default_link || '',
      };
    })
    .filter((action) => action.action_key);
}

function readCount(value, fallback) {
  if (value == null || value === '') return fallback;
  const count = Number(value);
  return Number.isFinite(count) ? count : fallback;
}

export function parseDayList(payload) {
  const days = Array.isArray(payload?.days) ? payload.days : [];
  const occupiedDates = Array.isArray(payload?.occupied_dates)
    ? payload.occupied_dates.filter(isDayKey)
    : days.map((day) => day.mission_date).filter(isDayKey);
  return {
    today: isDayKey(payload?.today) ? payload.today : localToday(),
    days,
    occupiedDates,
    total: readCount(payload?.total, days.length),
    limit: readCount(payload?.limit, days.length || 20),
    offset: readCount(payload?.offset, 0),
  };
}

export function parseMission(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return payload.id != null ? payload : null;
}

/** Create returns `{ missions }`. A single mission object is accepted too. */
export function parseSavedMission(payload) {
  if (Array.isArray(payload?.missions) && payload.missions.length) return payload.missions[0];
  return parseMission(payload);
}

export function parseConflicts(res) {
  const conflicts = res?.data?.conflicts;
  return Array.isArray(conflicts) ? conflicts : [];
}

export function isDateConflict(res) {
  if (parseConflicts(res).length > 0) return true;
  if (Number(res?.http_status) === 409) return true;
  return String(res?.message || '').toLowerCase().includes('already exists for');
}

export function parseDeleteResult(payload) {
  return String(payload?.result || '');
}

export function deleteResultMessage(result) {
  if (result === 'deleted') return 'Result: deleted. Nobody had opened this day yet.';
  if (result === 'deactivated') {
    return 'Result: deactivated. Players already have this day, so it was turned off instead of deleted. Their progress stays.';
  }
  return 'Daily mission updated.';
}

/**
 * Paths the app cannot open. A stored task link that is only one of these
 * is not an override; the action destination is used instead.
 */
const LEGACY_MISSION_PATHS = new Set([
  '/discover/daily_digest',
  '/matchsc',
  '/feeds',
  '/feed-creation',
  '/cheer-meter',
  '/app/streaks',
  '/genai',
  '/games',
  '/arcade',
]);

/** The signed-in profile destination. It is filled in by the app for any choose-a-team task. */
const PROFILE_FAN_JOURNEY = /^dugout:\/\/profile\/me\?focus=fan_journey&scroll=1$/;

/** Fan Wall for like-post and comment. The same competition is used on every tap. */
const FAN_WALL_LINK = [/^dugout:\/\/competition-posts(?:\?cid=\d+)?$/];

/** Links the admin GET fills in. Writing them back would lock that tap. */
const RESOLVED_APP_LINKS = {
  match_visit: [/^dugout:\/\/match\/\d+$/],
  player_rating: [/^dugout:\/\/match\/\d+\?tab=ratings$/],
  team_cheer: [/^dugout:\/\/home\?cid=\d+&match_id=\d+&focus=cheer-meter$/],
  post_like: FAN_WALL_LINK,
  comment: FAN_WALL_LINK,
  post_create: [/^dugout:\/\/create$/],
  run_connect: [/^dugout:\/\/arcade\/run-connect$/],
  snapshot: [/^dugout:\/\/arcade\/snap-shot$/],
  daily_digest: [/^dugout:\/\/stories\/[^/?#]+$/],
  dugout_ai_message: [/^dugout:\/\/duggy-ai\/\d+/],
  fan_journey_start: [/^dugout:\/\/profile\/me\?focus=fan_journey&scroll=1$/],
  fan_journey_favourite: [/^dugout:\/\/profile\/me\?focus=fan_journey&scroll=1$/],
  fan_journey_rival: [/^dugout:\/\/profile\/me\?focus=fan_journey&scroll=1$/],
};

/** Empty, the action's catalogue link, and old paths such as /feeds are not real overrides. */
export function isUnusedNavigationLink(link, catalogueLink) {
  const value = String(link || '').trim();
  if (!value) return true;
  const catalogue = String(catalogueLink || '').trim();
  if (catalogue && value === catalogue) return true;
  const path = value.split('?')[0].replace(/\/+$/, '') || '/';
  if (path === '/feeds' || path.startsWith('/feeds/')) return true;
  return LEGACY_MISSION_PATHS.has(path);
}

/**
 * True when this string is the link the app already chose for that tap
 * (next match, Fan Wall, fan-journey profile, and so on).
 */
export function isResolvedAppLink(link, actionKey) {
  const value = String(link || '').trim();
  if (!value) return false;
  if (PROFILE_FAN_JOURNEY.test(value)) return true;
  const patterns = RESOLVED_APP_LINKS[actionKey] || [];
  return patterns.some((pattern) => pattern.test(value));
}

/** How many In app boxes this count needs. An unfinished number stays at 1. */
export function navigationSlotCount(target) {
  const n = Number(target);
  if (!Number.isInteger(n) || n < 1) return 1;
  return n;
}

/**
 * Pad or preview the first `target` taps. Extras stay in the array until save.
 * A single string is the old one-link shape and applies to every tap.
 */
export function resizeNavigationLinks(links, target) {
  const count = navigationSlotCount(target);
  if (typeof links === 'string') {
    const value = links.trim();
    return Array.from({ length: count }, () => value);
  }
  const current = Array.isArray(links) ? links : [];
  return Array.from({ length: count }, (_, index) =>
    current[index] == null ? '' : String(current[index])
  );
}

/**
 * GET returns one effective link per tap, not the raw overrides.
 * A legacy single string was copied onto every tap.
 */
export function readEffectiveLinks(raw, target) {
  const count = navigationSlotCount(target);
  const slots = Array.from({ length: count }, () => '');
  if (typeof raw === 'string') {
    const value = raw.trim();
    if (value) slots.fill(value);
    return slots;
  }
  if (!Array.isArray(raw)) return slots;
  for (let index = 0; index < count && index < raw.length; index += 1) {
    const item = raw[index];
    if (typeof item === 'string' && item.trim()) slots[index] = item.trim();
  }
  return slots;
}

/**
 * Overrides the admin can edit. Resolved app links stay out of the inputs
 * so the next save sends null and the tap keeps updating.
 */
export function overridesFromEffectiveLinks(raw, target, actionKey, catalogueLink) {
  return readEffectiveLinks(raw, target).map((link) =>
    isUnusedNavigationLink(link, catalogueLink) || isResolvedAppLink(link, actionKey) ? '' : link
  );
}

/** Same rule as the backend when a task title is left empty. */
export function defaultTaskTitle(actionLabel, target) {
  if (!actionLabel) return '';
  const n = Number(target);
  return Number.isInteger(n) && n > 1 ? `${actionLabel} (${n})` : actionLabel;
}

export function emptyTask() {
  return {
    client_id: `task-${Math.random().toString(36).slice(2)}`,
    title: '',
    action_key: '',
    target_value: 1,
    navigation_link: [''],
    app_links: [],
    app_action_key: '',
  };
}

export function emptyForm(missionDate = '') {
  return {
    mission_date: missionDate,
    title: DEFAULT_MISSION_TITLE,
    subtitle: '',
    image_url: '',
    fan_energy_reward: 0,
    is_active: true,
    tasks: [emptyTask()],
  };
}

export function missionToForm(mission, { actionLinks } = {}) {
  if (!mission) return emptyForm();
  const tasks = Array.isArray(mission.tasks) ? mission.tasks : [];
  const links = actionLinks instanceof Map ? actionLinks : new Map();
  return {
    mission_date: mission.mission_date || '',
    title: mission.title || DEFAULT_MISSION_TITLE,
    subtitle: mission.subtitle || '',
    image_url: mission.image_url || '',
    fan_energy_reward: mission.fan_energy_reward ?? 0,
    is_active: !!mission.is_active,
    tasks: tasks.map((task, index) => {
      const actionKey = task.action_key || task.app_action_key || '';
      const catalogueLink = links.get(actionKey) || '';
      const appLinks = Array.isArray(task.app_links)
        ? readEffectiveLinks(task.app_links, task.target_value ?? 1)
        : readEffectiveLinks(task.navigation_link, task.target_value ?? 1);
      const overrides = Array.isArray(task.app_links)
        ? resizeNavigationLinks(task.navigation_link, task.target_value ?? 1)
        : overridesFromEffectiveLinks(task.navigation_link, task.target_value ?? 1, actionKey, catalogueLink);
      return {
        client_id: task.client_id || `saved-${task.id || index}-${index}`,
        title: task.title || '',
        action_key: task.action_key || '',
        target_value: task.target_value ?? 1,
        navigation_link: overrides,
        app_links: appLinks,
        app_action_key: actionKey,
      };
    }),
  };
}

/** Copy of another day's mission onto a new date (the duplicate button). */
export function copyMissionToForm(mission, missionDate, options) {
  const form = missionToForm(mission, options);
  return {
    ...form,
    mission_date: missionDate,
    is_active: true,
    tasks: form.tasks.map((task) => ({
      ...task,
      client_id: emptyTask().client_id,
      navigation_link: [...(task.navigation_link || [])],
      app_links: [...(task.app_links || [])],
    })),
  };
}

function wholeNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

export function validateForm(form, { actionKeys } = {}) {
  if (!isDayKey(form.mission_date)) return 'Pick the date for this mission.';
  if (String(form.title || '').trim().length > 160) return 'Mission name must be 160 characters or fewer.';
  const reward = wholeNumber(form.fan_energy_reward);
  if (reward == null || reward < 0) return 'Reward must be a whole number of 0 or more.';
  if (!Array.isArray(form.tasks) || form.tasks.length === 0) return 'Add at least one task.';
  if (form.tasks.length > MAX_TASKS) return `A day can have at most ${MAX_TASKS} tasks.`;
  for (let i = 0; i < form.tasks.length; i += 1) {
    const task = form.tasks[i];
    if (!task.action_key) return `Task ${i + 1}: pick an action.`;
    if (actionKeys && !actionKeys.has(task.action_key)) {
      return `Task ${i + 1}: “${task.action_key}” is not in the action list.`;
    }
    const target = wholeNumber(task.target_value);
    if (target == null || target < 1) return `Task ${i + 1}: how many times must be 1 or more.`;
    if (String(task.title || '').trim().length > 160) {
      return `Task ${i + 1}: title must be 160 characters or fewer.`;
    }
    const taps = resizeNavigationLinks(task.navigation_link, target);
    for (let tap = 0; tap < taps.length; tap += 1) {
      if (String(taps[tap] || '').trim().length > 500) {
        return `Task ${i + 1}: In app ${tap + 1} must be 500 characters or fewer.`;
      }
    }
  }
  return '';
}

export function formToPayload(form, { includeTasks = true, actionLabels, actionLinks } = {}) {
  const labels = actionLabels instanceof Map ? actionLabels : new Map();
  const links = actionLinks instanceof Map ? actionLinks : new Map();
  const payload = {
    mission_date: form.mission_date,
    title: String(form.title || '').trim() || DEFAULT_MISSION_TITLE,
    subtitle: String(form.subtitle || '').trim() || null,
    image_url: String(form.image_url || '').trim() || null,
    fan_energy_reward: Number(form.fan_energy_reward) || 0,
    is_active: !!form.is_active,
  };
  if (includeTasks) {
    payload.tasks = (form.tasks || []).map((task, index) => {
      const typedTitle = String(task.title || '').trim();
      const autoTitle = defaultTaskTitle(labels.get(task.action_key), task.target_value);
      const catalogueLink = links.get(task.action_key);
      const target = Number(task.target_value);
      return {
        task_key: `task_${index + 1}`,
        title: typedTitle || autoTitle,
        action_key: task.action_key,
        target_value: target,
        navigation_link: resizeNavigationLinks(task.navigation_link, target).map((link) =>
          isUnusedNavigationLink(link, catalogueLink) ? null : String(link).trim()
        ),
        sort_order: index + 1,
      };
    });
  }
  return payload;
}

/** POST always wraps missions. A mission object at the root is rejected. */
export function buildCreateRequest(form, { actionLabels, actionLinks, override = false } = {}) {
  return {
    override: !!override,
    missions: [formToPayload(form, { includeTasks: true, actionLabels, actionLinks })],
  };
}

/** PUT is one mission. `override` matters when the date moves onto another mission's day. */
export function buildUpdateRequest(form, { actionLabels, actionLinks, override = false, includeTasks = true } = {}) {
  return {
    override: !!override,
    ...formToPayload(form, { includeTasks, actionLabels, actionLinks }),
  };
}
